import type { ContractTemplate, LaborContract } from './types';

// Mint-LaborContract · tab Tổng quan (code board khối TODO / life / rules / KP). Không lương, không CCCD.

export const CONTRACT_TABS = [
  { key: 'ov', label: 'Tổng quan', n: 5, warn: true },
  { key: 'iss', label: 'Phát hành' },
  { key: 'tpl', label: 'Mẫu hợp đồng', n: 17 },
  { key: 'issued', label: 'Đã phát hành', n: 9 },
] as const;
export type ContractTab = (typeof CONTRACT_TABS)[number]['key'];

export type TodoTone = 'warn' | 'err' | 'mute' | 'pri';
export interface ContractTodo {
  tag: string;
  tone: TodoTone;
  k: string;
  sub: string;
  act: string;
  /** tab mở khi bấm; '' = báo toast chưa mở ghi */
  to: ContractTab | '';
}

export const CONTRACT_TODO: readonly ContractTodo[] = [
  { tag: 'Sắp hết hạn', tone: 'warn', k: 'NhiTHT · HN-HDLD-12M-2025-017', sub: 'Hết 15/Nov/2026 · đã ký XĐTH 1 lần → gợi ý HDLD-36M', act: 'Gia hạn', to: 'iss' },
  { tag: 'Sắp hết hạn', tone: 'warn', k: 'AnNV · HN-HDLD-12M-2025-030', sub: 'Hết 09/Nov/2026 · đang có QĐ điều chuyển sang TRD-DSP chờ duyệt', act: 'Gia hạn', to: 'iss' },
  { tag: 'Bắt buộc KXĐ', tone: 'err', k: 'THANHTTK · đã ký 2 HĐ xác định thời hạn', sub: 'Hết 30/Nov/2026 · lần tới phải là HĐ không xác định thời hạn (Điều 20)', act: 'Lập HĐ KXĐ', to: 'iss' },
  { tag: 'Chờ phát hành', tone: 'mute', k: 'Võ Thị Lan · HN-HDTV-2026-007', sub: 'Bản NHÁP vì mẫu HDTV-02M còn Draft · nhận việc 03/Nov', act: 'Duyệt mẫu', to: 'tpl' },
  { tag: 'Hết thử việc', tone: 'pri', k: 'Trần Quốc Bảo · thử việc đến 19/Dec/2026', sub: 'Đánh giá cuối thử việc → HĐLĐ 12 tháng', act: 'Xem', to: 'issued' },
];

export const CONTRACT_RULES: readonly (readonly [string, string])[] = [
  ['Điều 20 · số lần ký xác định thời hạn', 'Đã ký 2 HĐ xác định thời hạn → lần tiếp theo chặn mẫu 12M/36M, chỉ cho KXĐ'],
  ['Điều 25 · thời gian thử việc', 'Mẫu 2 tháng chỉ cho chức danh cần trình độ cao đẳng trở lên; dưới mức đó tối đa 30 ngày'],
  ['Lương tối thiểu vùng I', 'Lương chính dưới mức tối thiểu vùng → không phát hành'],
  ['Mẫu chưa duyệt', 'Mẫu Draft vẫn ghép và in được nhưng bản phát hành đánh dấu NHÁP'],
];

export const CONTRACT_MORE: readonly (readonly [string, string])[] = [
  ['Nạp 5 mẫu HĐ lao động chuẩn', 'HDTV-02M · HDDT-06M · HDLD-12M · HDLD-36M · HDLD-KXD (Draft)'],
  ['Cấu hình thông tin công ty', 'Pháp nhân, đại diện ký, địa chỉ — dùng chung Cài đặt › Công ty'],
  ['Xuất danh sách hợp đồng (Excel)', 'Theo bộ lọc đang chọn'],
];

export type ContractKey = 'draft' | 'wait' | 'signed' | 'soon' | 'end';
/** Trạng thái hiển thị (STV của board): hết hạn ≤ 30 ngày tính là "soon". */
export function contractKey(c: LaborContract): ContractKey {
  if (c.status === 'Draft') return 'draft';
  if (c.status === 'Issued') return 'wait';
  if (c.status === 'Terminated') return 'end';
  return c.daysLeft !== null && c.daysLeft <= 30 ? 'soon' : 'signed';
}

export interface ContractKpi { k: string; v: string; u: string; tone?: 'warn' | 'err' | 'ok' }

// Board đếm theo tổng mẫu của tab Mẫu hợp đồng (TPL0 = 17), không theo số mẫu đang nạp
const TPL_TOTAL = CONTRACT_TABS[2].n;

export function contractKpis(cs: readonly LaborContract[], _tpls: readonly ContractTemplate[]): ContractKpi[] {
  const key = cs.map(contractKey);
  const n = (...ks: ContractKey[]) => key.filter((k) => ks.includes(k)).length;
  const soon = n('soon');
  // Mẫu hợp đồng lao động đều ở trạng thái Draft trong bản mẫu (chưa ai duyệt)
  return [
    { k: 'Mẫu đang dùng được', v: `0/${TPL_TOTAL}`, u: `${TPL_TOTAL} mẫu Draft chờ duyệt`, tone: 'warn' },
    { k: 'Sắp hết hạn ≤ 30 ngày', v: String(soon), u: 'NhiTHT · AnNV', tone: soon ? 'warn' : 'ok' },
    { k: 'Chờ ký / nháp', v: String(n('draft', 'wait')), u: 'thử việc nhận việc 03/Nov' },
    { k: 'Bị chặn bởi Điều 20', v: '1', u: 'THANHTTK → bắt buộc KXĐ', tone: 'err' },
  ];
}

export function contractLife(cs: readonly LaborContract[]): { k: string; sub: string; c: number }[] {
  const key = cs.map(contractKey);
  const n = (...ks: ContractKey[]) => key.filter((k) => ks.includes(k)).length;
  return [
    { k: 'Nháp', sub: 'ghép mẫu Draft hoặc chưa đủ trường', c: n('draft') },
    { k: 'Phát hành', sub: 'sinh số HĐ · in 02 bản', c: n('wait') },
    { k: 'Đã ký', sub: 'tải bản ký vào Tài liệu bắt buộc · ghi ContractRef vào hồ sơ', c: n('signed', 'soon') },
    { k: 'Gia hạn', sub: 'từ danh sách sắp hết hạn · kiểm Điều 20', c: n('soon') },
    { k: 'Chấm dứt', sub: 'qua Quyết định nghỉ việc', c: n('end') },
  ];
}

/** Bảng phải 1920 · "Sắp hết hạn ≤ 30 ngày" (board: soonL = Signed + còn ≤ 30 ngày). */
export function contractSoon(cs: readonly LaborContract[]): { k: string; left: string; sub: string }[] {
  return cs
    .filter((c) => c.status === 'Signed' && c.daysLeft !== null && c.daysLeft <= 30)
    .map((c) => ({ k: `${c.name} · ${c.seat}`, left: `còn ${c.daysLeft} ngày`, sub: `${c.no} · hết ${c.to} · ${c.nth >= 2 ? 'lần sau bắt buộc KXĐ' : `ký lần ${c.nth}`}` }));
}

export const CONTRACT_DOCK_TITLE = 'Hạn & mẫu chờ duyệt';
export const CONTRACT_DOCK_NOTE = 'Soạn [HRD-MGR · kiêm] HANHLTM → duyệt [BOD-DIR] PHILIP · người duyệt mẫu chờ anh chốt';

// ===== Tab Mẫu hợp đồng (code board TPL0) =====
export type TemplateGroup = 'hdld' | 'dv' | 'qd' | 'khac';
export interface TemplateRow { code: string; name: string; type: string; term: string; version: string; law: string; group: TemplateGroup }
const T = (code: string, name: string, type: string, term: string, version: string, law: string, group: TemplateGroup): TemplateRow => ({ code, name, type, term, version, law, group });
export const TEMPLATE_CATALOG: readonly TemplateRow[] = [
  T('HDTV-02M', 'HĐ thử việc 2 tháng', 'ThuViec', '2 tháng', 'v1.0', 'BLLĐ 2019 Điều 24–27', 'hdld'),
  T('HDDT-06M', 'HĐ đào tạo nghề 6 tháng', 'DaoTaoNghe', '6 tháng', 'v1.0', 'BLLĐ 2019 Điều 61–62', 'hdld'),
  T('HDLD-12M', 'HĐLĐ xác định thời hạn 12 tháng', 'XacDinhThoiHan', '12 tháng', 'v1.0', 'BLLĐ 2019 Điều 20', 'hdld'),
  T('HDLD-36M', 'HĐLĐ xác định thời hạn 36 tháng', 'XacDinhThoiHan', '36 tháng', 'v1.0', 'BLLĐ 2019 Điều 20', 'hdld'),
  T('HDLD-KXD', 'HĐLĐ không xác định thời hạn', 'KhongXacDinhThoiHan', '—', 'v1.0', 'BLLĐ 2019 Điều 20', 'hdld'),
  T('HDDV-CR', 'HĐ dịch vụ (CR)', 'DichVu', '—', 'v1.0', 'BLDS 2015', 'dv'),
  T('HDDV-NC', 'HĐ dịch vụ (NC)', 'DichVu', '—', 'v1.0', 'BLDS 2015', 'dv'),
  T('HDDV-CR-BI', 'HĐ dịch vụ song ngữ (CR)', 'DichVu', '—', 'v1.0', 'BLDS 2015', 'dv'),
  T('HDDV-NC-BI', 'HĐ dịch vụ song ngữ (NC)', 'DichVu', '—', 'v1.0', 'BLDS 2015', 'dv'),
  T('HDDL-AG', 'HĐ đại lý (AG)', 'DichVu', '—', 'v1.6', 'Luật Thương mại 2005', 'dv'),
  T('GVT-PL28', 'Giấy vận tải (PL28)', 'GiayVanTai', '—', 'v1.0', 'NĐ vận tải đường bộ', 'khac'),
  T('QD-BONHIEM', 'Quyết định bổ nhiệm', 'BoNhiem', '—', 'v1.0', 'Quy chế nhân sự', 'qd'),
  T('TB-THUMOI', 'Thư mời nhận việc', 'ThuMoi', '—', 'v1.0', 'Quy chế nhân sự', 'qd'),
  T('QD-TANGLUONG', 'Quyết định điều chỉnh lương', 'TangLuong', '—', 'v1.0', 'Quy chế lương', 'qd'),
  T('QD-THANGCAP', 'Quyết định thăng cấp', 'ThangCap', '—', 'v1.0', 'Quy chế nhân sự', 'qd'),
  T('QD-DIEUCHUYEN', 'Quyết định điều chuyển', 'DieuChuyen', '—', 'v1.0', 'BLLĐ 2019 Điều 29', 'qd'),
  T('QD-KIEMNHIEM', 'Quyết định kiêm nhiệm', 'KiemNhiem', '—', 'v1.0', 'Quy chế nhân sự', 'qd'),
];
export const TEMPLATE_GROUPS: readonly (readonly [TemplateGroup | 'all', string])[] = [
  ['all', 'Tất cả'], ['hdld', 'HĐ lao động'], ['dv', 'Dịch vụ'], ['qd', 'Quyết định & thư'], ['khac', 'Khác'],
];
export const TEMPLATE_COLS: readonly (readonly [string, string])[] = [
  ['Mã · tên mẫu', '26%'], ['Loại', '15%'], ['Thời hạn', '9%'], ['Phiên bản', '12%'], ['Căn cứ', '14%'], ['Trạng thái', '8%'], ['', '16%'],
];

// ===== Tab Đã phát hành (code board isF / isCols / STV) =====
export const ISSUED_FILTERS: readonly (readonly [ContractKey | 'all', string])[] = [
  ['all', 'Tất cả'], ['draft', 'Nháp'], ['wait', 'Chờ ký'], ['signed', 'Đã ký'], ['soon', 'Sắp hết hạn'], ['end', 'Đã chấm dứt'],
];
export const ISSUED_COLS: readonly (readonly [string, string])[] = [
  ['Số HĐ · mẫu', '21%'], ['Nhân viên · ghế', '21%'], ['Loại', '14%'], ['Hiệu lực', '20%'], ['Trạng thái', '11%'], ['', '13%'],
];
export const ISSUED_STATUS: Record<ContractKey, { label: string; tone: 'ok' | 'warn' | 'mute' }> = {
  draft: { label: 'Nháp · mẫu chưa duyệt', tone: 'mute' },
  wait: { label: 'Chờ ký', tone: 'warn' },
  signed: { label: 'Đã ký', tone: 'ok' },
  soon: { label: 'Sắp hết hạn', tone: 'warn' },
  end: { label: 'Đã chấm dứt', tone: 'mute' },
};
/** Nút hành động theo trạng thái (board isAll.acts); mẫu còn Draft thì chờ duyệt mẫu. */
export function issuedAction(key: ContractKey): string | null {
  return key === 'draft' ? 'Chờ mẫu được duyệt' : key === 'wait' ? 'Đánh dấu đã ký' : key === 'soon' ? 'Gia hạn' : key === 'end' ? 'Xem QĐ nghỉ việc' : null;
}
